import React, { createContext, useState, useEffect, useContext, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { getSalonConfig, updateSalonConfig } from '../firebase/salon';
import { useUser } from './UserContext';

// Criação do contexto
const SalonContext = createContext(null);

// Hook personalizado para usar o contexto
export const useSalon = () => {
  const context = useContext(SalonContext);
  if (context === null) {
    throw new Error('useSalon deve ser usado dentro de um SalonProvider');
  }
  return context;
};

// Provedor do contexto
export const SalonProvider = ({ children, salonId: propSalonId }) => {
  const params = useParams();
  const urlSalonId = params?.salonId;
  const { getCurrentSalon, getCurrentRole, hasPermission } = useUser();
  const [salonConfig, setSalonConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Determinar qual salonId usar (prop ou URL)
  const salonId = propSalonId || urlSalonId;
  
  // Obter informações do salão atual do UserContext
  const currentSalon = salonId ? getCurrentSalon(salonId) : null;
  const userRole = salonId ? getCurrentRole(salonId) : null;

  // Carregar configuração do salão
  useEffect(() => {
    const loadSalonConfig = async () => {
      if (!salonId) {
        setSalonConfig(null);
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        setError(null);
        
        const config = await getSalonConfig(salonId);
        
        // Se já temos algumas informações do UserContext, mesclar com as informações do salão
        if (currentSalon) {
          setSalonConfig({
            ...config,
            ...currentSalon,
            role: userRole
          });
        } else {
          setSalonConfig(config);
        }
      } catch (err) {
        console.error('Erro ao carregar configuração do salão:', err);
        setError(err.message || 'Erro ao carregar configuração do salão');
      } finally {
        setLoading(false);
      }
    };

    loadSalonConfig();
  }, [salonId, currentSalon, userRole]);

  // Função para verificar se o usuário tem permissão para uma ação específica
  const checkPermission = useCallback(async (permission) => {
    return hasPermission(permission, salonId);
  }, [hasPermission, salonId]);

  // Função para atualizar a configuração do salão
  const updateSalon = useCallback(async (configUpdates) => {
    if (!salonId) return false;
    
    // Verificar permissão para atualizar o salão
    const canUpdate = await checkPermission('manage_salon_settings');
    if (!canUpdate) {
      setError('Você não tem permissão para atualizar as configurações do salão');
      return false;
    }
    
    try {
      setError(null);
      
      // Atualizar no Firestore
      await updateSalonConfig(salonId, configUpdates);
      
      // Atualizar estado local
      setSalonConfig(prev => ({
        ...prev,
        ...configUpdates,
        updatedAt: new Date()
      }));
      
      return true;
    } catch (err) {
      console.error('Erro ao atualizar configuração do salão:', err);
      setError(err.message || 'Erro ao atualizar configuração do salão');
      return false;
    }
  }, [salonId, checkPermission]);

  // Objeto com valores e funções a serem disponibilizadas pelo contexto
  const value = {
    salonId,
    salonConfig,
    loading,
    error,
    updateSalon,
    userRole,
    checkPermission
  };

  return (
    <SalonContext.Provider value={value}>
      {children}
    </SalonContext.Provider>
  );
};

export default SalonContext; 